/* eslint-disable no-console, no-eval, react/no-danger */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Element, scroller } from 'react-scroll';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import classNames from 'classnames';
import Loader from 'components/Loader';
import { ccpaOptOutEnabled } from 'helpers/ConfigHelper';
import {
  CCPA_DO_NOT_SELL_CHECKED,
  CCPA_DO_NOT_SELL_SEEN,
  getPrivacyParameterCookie,
  isStaticRender,
  setPrivacyParameterCookie,
} from 'helpers/ContentHelper';
import {
  Events,
  getTealiumFooterPageData,
  handleAnalytics,
  PageNames,
} from '@foxcorp/delta-web-analytics';
import Seo from 'componentsV2/Seo';
import { helperGoToShowDetail } from 'helpers/RouteHelper';
import { fetchArticle } from './ArticleActions';
import {
  getArticleApiUrl,
  getPurifyConfig,
  IFRAME_TEST_PATTERN,
  SCRIPT_TEST_PATTERN,
} from './ArticleHelpers';
import style from './Article.module.scss';

const CCPA_DO_NOT_SELL = 'CCPA DO NOT SELL';
const CCPA_DO_NOT_SELL_CHECKBOX_IDENTIFIER = '[Toggle Goes Here]';

class Article extends PureComponent {
  static handleEmbedScripts(articleBody) {
    const script = SCRIPT_TEST_PATTERN.exec(articleBody);
    try {
      if (window && script && script[1]) window.eval(script[1]);
    } catch (e) {
      console.debug('Embed Script error: ', e);
    }
    return script && script[0] ? articleBody.replace(script[0], '') : articleBody;
  }

  constructor(props) {
    super(props);

    this.state = {
      isDoNotSellInfoChecked: getPrivacyParameterCookie() === CCPA_DO_NOT_SELL_CHECKED,
    };
  }

  componentDidMount() {
    const { accessToken, deviceId, fetchArticle, pageViewEvent, seriesType, url } = this.props;
    if (url && fetchArticle && !isStaticRender()) {
      fetchArticle(url, accessToken, deviceId);
    }
    pageViewEvent({ pageName: PageNames.ARTICLE, seriesType });
    this.scrollToTargetElement();
    (function loadWidget() {
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      const host = document.location.protocol === 'http:' ? 'cdn.snapapp.com' : 'scdn.snapapp.com';
      s.src = `//${host}/widget/widget.js`;
      s.id = 'eeload';
      document.getElementsByTagName('head')[0].appendChild(s);
    })();
  }

  componentDidUpdate(prevProps) {
    const { url: prevUrl, panels: prevPanels, errors: prevErrors } = prevProps;
    const {
      fetchArticle,
      url,
      pageViewEvent,
      setErrors,
      panels,
      errors,
      accessToken,
      deviceId,
      disableScrollEvent,
    } = this.props;
    const getPanelName = _.get(panels, 'member[0].items.member[0].name', '');
    if (getPanelName === CCPA_DO_NOT_SELL) {
      handleAnalytics({
        event: Events.CCPA_PAGE_LANDING,
        payload: {
          pageName: `ccpa-page`,
          pageType: `ccpa`,
        },
      });
      const isNotOptedOutCCPA = getPrivacyParameterCookie() !== CCPA_DO_NOT_SELL_CHECKED;
      if (ccpaOptOutEnabled() && isNotOptedOutCCPA) {
        setPrivacyParameterCookie({ value: CCPA_DO_NOT_SELL_SEEN, options: { path: '/' } });
      }
    }
    if (prevUrl !== url) {
      pageViewEvent({ pageName: PageNames.ARTICLE });
      fetchArticle(url, accessToken, deviceId);
    }
    if (!prevPanels && panels) {
      const articleName = _.get(panels, 'member[0].items.member[0].name', false);
      const pageViewData = getTealiumFooterPageData(articleName, true);
      if (pageViewData) pageViewEvent({ ...pageViewData });
      if (!disableScrollEvent) this.scrollToTargetElement();
    }
    if (prevErrors !== errors) {
      setErrors(errors);
    }
  }

  scrollToTargetElement() {
    const { scrollElement } = this.props;
    if (scrollElement) {
      const scrollOptions = {
        duration: 300,
        offset: -90,
        smooth: true,
      };
      scroller.scrollTo(scrollElement, scrollOptions);
    }
  }

  seoHeaderWrapper(headline) {
    const { primaryHeadline } = this.props;
    return primaryHeadline ? (
      <h1 className={style.title}>{headline}</h1>
    ) : (
      <h2 className={style.title}>{headline}</h2>
    );
  }

  shouldRenderItemBasedOnContextAndShowId(item) {
    const { showId } = this.props;
    const id = _.get(item, 'context.id', null);
    if (!showId) return true;
    return id === showId;
  }

  handleDoNotSellInfoChecked = () => {
    const { isDoNotSellInfoChecked } = this.state;
    this.setState({ isDoNotSellInfoChecked: !isDoNotSellInfoChecked });
  };

  renderDoNotSellInfoCheckbox = () => {
    const { isDoNotSellInfoChecked } = this.state;
    const ccpaDoNotSellLabel = _.get(window.Language, 'ccpa_do_not_sell_label');
    const ccpaDoNotSellConfirmation = _.get(window.Language, 'ccpa_do_not_sell_confirmation');

    if (isDoNotSellInfoChecked) {
      setPrivacyParameterCookie({ value: CCPA_DO_NOT_SELL_CHECKED, options: { path: '/' } });
    } else {
      setPrivacyParameterCookie({ value: CCPA_DO_NOT_SELL_SEEN, options: { path: '/' } });
    }

    return (
      <div className={style.checkboxContainer}>
        <div className={style.doNotSellInfoContainer}>
          <h3 className={style.doNotSellInfoText}>{ccpaDoNotSellLabel}</h3>
          {isDoNotSellInfoChecked && (
            <p className={style.doNotSellConfirmationText}>{ccpaDoNotSellConfirmation}</p>
          )}
        </div>
        <label className={style.checkboxLabel}>
          <input
            name="doNotSellInfo"
            aria-label="CCPA do not sell info"
            type="checkbox"
            checked={isDoNotSellInfoChecked}
            onChange={this.handleDoNotSellInfoChecked}
          />
          <span />
        </label>
      </div>
    );
  };

  renderArticle(item) {
    const { showId, scrollElement } = this.props;
    const { headline, articleBody } = item;
    const getPanelName = _.get(item, 'name', '');
    const isDoNotSellPage = getPanelName === CCPA_DO_NOT_SELL;
    const containerClass = classNames([style.container, isDoNotSellPage && style.dnsContainer]);
    let articleBodySplit;
    let sanitizedBody = Article.handleEmbedScripts(articleBody);

    // check article belongs to show.
    if (!this.shouldRenderItemBasedOnContextAndShowId(item)) {
      if (!isStaticRender()) {
        helperGoToShowDetail(showId);
      }
      return null;
    }

    // sanitize article body if it doesn't have an iframe.
    if (!IFRAME_TEST_PATTERN.test(articleBody)) {
      sanitizedBody = DOMPurify.sanitize(articleBody, getPurifyConfig());
    }

    if (isDoNotSellPage) {
      articleBodySplit = articleBody.split(CCPA_DO_NOT_SELL_CHECKBOX_IDENTIFIER);
    }

    const renderArticleBody = () => {
      if (isDoNotSellPage && articleBodySplit && articleBodySplit.length > 1) {
        const beginningSanitizedBody = Article.handleEmbedScripts(articleBodySplit[0]);
        const endingSanitizedBody = Article.handleEmbedScripts(articleBodySplit[1]);
        return (
          <>
            <div
              className={style.body}
              dangerouslySetInnerHTML={{ __html: beginningSanitizedBody }}
            />
            {ccpaOptOutEnabled() && this.renderDoNotSellInfoCheckbox()}
            <div className={style.body} dangerouslySetInnerHTML={{ __html: endingSanitizedBody }} />
          </>
        );
      }

      return articleBody ? (
        <div className={style.body} dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
      ) : null;
    };

    return (
      <div key="article-detail" className={containerClass}>
        <Element name={scrollElement}>
          {headline ? this.seoHeaderWrapper(headline) : null}
          {renderArticleBody()}
        </Element>
      </div>
    );
  }

  renderPanels() {
    const { panels, setLoading } = this.props;
    if (!panels || !panels.member || panels.length === 0)
      return <Loader setLoading={setLoading} center />;

    return panels.member.map(panel => {
      const { items, panelType } = panel;
      const {
        member: [item],
      } = items;
      switch (panelType) {
        case 'articleDetail':
          return this.renderArticle(item);
        default:
          return null;
      }
    });
  }

  render() {
    const { seo, containerId } = this.props;
    return (
      <div id={containerId}>
        <Seo seo={seo} />
        {this.renderPanels()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    panels: state.article.panels,
    errors: state.article.errors,
    seo: state.article.seo,
  };
}

Article.fetchData = ({ store, params, accessToken }) => {
  const id = params.articleId;
  const url = getArticleApiUrl(id);
  return store.dispatch(fetchArticle(url, accessToken));
};

Article.defaultProps = {
  accessToken: null,
  containerId: null,
  deviceId: null,
  errors: null,
  panels: null,
  primaryHeadline: true,
  scrollElement: null,
  seo: {},
  seriesType: '',
  setErrors: () => {},
  setLoading: null,
  showId: null,
  theme: {},
  url: null,
  disableScrollEvent: false,
};

Article.propTypes = {
  accessToken: PropTypes.string,
  containerId: PropTypes.string,
  deviceId: PropTypes.string,
  errors: PropTypes.number,
  fetchArticle: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func.isRequired,
  panels: PropTypes.shape({ member: PropTypes.array }),
  primaryHeadline: PropTypes.bool,
  scrollElement: PropTypes.string,
  seo: PropTypes.shape({}),
  seriesType: PropTypes.string,
  setErrors: PropTypes.func,
  setLoading: PropTypes.func,
  showId: PropTypes.string,
  theme: PropTypes.shape({}),
  url: PropTypes.string,
  disableScrollEvent: PropTypes.bool,
};

export default connect(mapStateToProps, { fetchArticle })(Article);
